<script setup lang="ts">
  import BlockTitle from './BlockTitle/BlockTitle.vue'
  import RelationshipField from './fields/RelationshipField.vue'
  import FirstNameField from './fields/FirstNameField.vue'
  import LastNameField from './fields/LastNameField.vue'
  import FullNameField from './fields/FullNameField.vue'
  import BirthDateField from './fields/BirthDateField.vue'
  import GenderRadios from './fields/GenderRadios.vue'
  import NationalityField from './fields/NationalityField.vue'
  import CountryField from './fields/CountryField.vue'
  import PassportField from './fields/PassportField.vue'
  import PassportExpiryField from './fields/PassportExpiryField.vue'
  import MaritalStatusField from './fields/MaritalStatusField.vue'
  import type { CountryWithDetailsType } from '@/shared/types/other.types'

  type Mode = 'default' | 'ocr' | 'visa'

  interface Props {
    mode: Mode
    v$: any
    errors: Record<string, any>
    isHolder: boolean
    ocrIsAvailable: boolean
    ocrProcessing: boolean
    ocrUploadSuccess: boolean
    isFileUploaderShown: boolean
    isOcrManualMode: boolean
    isMaritalStatusEnabled: boolean
    isDisabledProperties: boolean
    localCountries: CountryWithDetailsType[]
    localNationalities: CountryWithDetailsType[]
    relations: Array<{ code: string; desc: string }>
    maritalStatuses: Array<{ code: string; name: string }>
    passportMinDate: Date
    findCountry: (s: string) => CountryWithDetailsType[]
    findNationality: (s: string) => CountryWithDetailsType[]
  }

  const firstName = defineModel<string>('firstName', { required: true })
  const lastName = defineModel<string>('lastName', { required: true })
  const fullName = defineModel<string | undefined>('fullName')
  const birthDate = defineModel<Date | null>('birthDate', { required: true })
  const gender = defineModel<number>('gender', { required: true })
  const passport = defineModel<string>('passport', { required: true })
  const passportExpiryDate = defineModel<Date | null>('passportExpiryDate', { required: true })
  const maritalStatus = defineModel<string | null | undefined>('maritalStatus')
  const relationship = defineModel<string | null>('relationship', { required: true })
  const selectedCountry = defineModel<CountryWithDetailsType | undefined>('selectedCountry')
  const selectedNationality = defineModel<CountryWithDetailsType | undefined>('selectedNationality')

  defineProps<Props>()
</script>

<template>
  <div class="visitor-form__inputs">
    <BlockTitle
      v-if="!isFileUploaderShown || isOcrManualMode"
      icon="passport"
      :text="$t('visitors.fill_visitors_info.passport_details_block.header')"
    />

    <template v-if="mode === 'visa'">
      <RelationshipField
        v-if="!isHolder"
        v-model="relationship"
        :relations="relations"
        :errors="errors.relationship"
        :is-valid="!v$.relationship.$dirty && !v$.relationship.$invalid"
      />
      <NationalityField
        v-model="selectedNationality"
        :countries="localNationalities"
        :errors="errors.nationality"
        :is-valid="!v$.nationality.$dirty && !v$.nationality.$invalid"
        disabled
        :filter-function="findNationality"
        :label="$t('qlm_nationality')"
      />
      <FullNameField v-model="fullName" />
      <BirthDateField
        v-model="birthDate"
        :errors="errors.birthDate"
        :is-valid="!v$.birthDate.$invalid"
        disabled
        :loading="ocrProcessing"
      />
      <GenderRadios
        v-model="gender"
        disabled
        :ocr-is-available="ocrIsAvailable"
        :loading="ocrProcessing"
      />
      <PassportField
        v-model="passport"
        :errors="errors.passport"
        :is-valid="!v$.passport.$invalid"
        disabled
        :loading="ocrProcessing"
      />
      <PassportExpiryField
        v-model="passportExpiryDate"
        :errors="errors.passportExpiryDate"
        :is-valid="!v$.passportExpiryDate.$invalid"
        :passport-min-date="passportMinDate"
        :loading="ocrProcessing"
      />
      <CountryField
        v-model="selectedCountry"
        :countries="localCountries"
        :errors="errors.country"
        :is-valid="!v$.country.$dirty && !v$.country.$invalid"
        :filter-function="findCountry"
      />
      <MaritalStatusField
        v-if="isMaritalStatusEnabled"
        v-model="maritalStatus"
        :marital-statuses="maritalStatuses"
        :errors="errors.maritalStatus"
        :is-valid="!v$.maritalStatus.$invalid"
      />
    </template>

    <template v-else-if="mode === 'ocr'">
      <FirstNameField
        v-model="firstName"
        :errors="errors.firstName"
        :is-valid="!v$.firstName.$invalid"
        :loading="ocrProcessing"
      />
      <LastNameField
        v-model="lastName"
        :errors="errors.lastName"
        :is-valid="!v$.lastName.$invalid"
        :loading="ocrProcessing"
      />
      <BirthDateField
        v-model="birthDate"
        :errors="errors.birthDate"
        :is-valid="!v$.birthDate.$invalid"
        :disabled="false"
        :loading="ocrProcessing"
      />
      <GenderRadios
        v-model="gender"
        :disabled="false"
        :ocr-is-available="ocrIsAvailable"
        :loading="ocrProcessing"
      />
      <PassportField
        v-model="passport"
        :errors="errors.passport"
        :is-valid="!v$.passport.$invalid"
        :disabled="false"
        :loading="ocrProcessing"
      />
      <PassportExpiryField
        v-model="passportExpiryDate"
        :errors="errors.passportExpiryDate"
        :is-valid="!v$.passportExpiryDate.$invalid"
        :passport-min-date="passportMinDate"
        :loading="ocrProcessing"
      />
      <CountryField
        v-model="selectedCountry"
        :countries="localCountries"
        :errors="errors.country"
        :is-valid="!v$.country.$dirty && !v$.country.$invalid"
        :filter-function="findCountry"
      />
      <MaritalStatusField
        v-if="isMaritalStatusEnabled"
        v-model="maritalStatus"
        :marital-statuses="maritalStatuses"
        :errors="errors.maritalStatus"
        :is-valid="!v$.maritalStatus.$invalid"
      />
      <AlertQC
        v-if="ocrUploadSuccess"
        variant="brand"
        :subtitle="$t('visitors.fill_visitors_info.check_ocr_info.text')"
        with-icon
      />
    </template>

    <template v-else>
      <RelationshipField
        v-if="!isHolder"
        v-model="relationship"
        :relations="relations"
        :errors="errors.relationship"
        :is-valid="!v$.relationship.$dirty && !v$.relationship.$invalid"
      />
      <NationalityField
        v-model="selectedNationality"
        :countries="localNationalities"
        :errors="errors.nationality"
        :is-valid="!v$.nationality.$dirty && !v$.nationality.$invalid"
        :disabled="false"
        :filter-function="findNationality"
        :label="$t('qlm_nationality')"
      />
      <FirstNameField
        v-model="firstName"
        :errors="errors.firstName"
        :is-valid="!v$.firstName.$invalid"
        :loading="ocrProcessing"
      />
      <LastNameField
        v-model="lastName"
        :errors="errors.lastName"
        :is-valid="!v$.lastName.$invalid"
        :loading="ocrProcessing"
      />
      <BirthDateField
        v-model="birthDate"
        :errors="errors.birthDate"
        :is-valid="!v$.birthDate.$invalid"
        :disabled="false"
        :loading="ocrProcessing"
      />
      <GenderRadios
        v-model="gender"
        :disabled="false"
        :ocr-is-available="ocrIsAvailable"
        :loading="ocrProcessing"
      />
      <CountryField
        v-model="selectedCountry"
        :countries="localCountries"
        :errors="errors.country"
        :is-valid="!v$.country.$dirty && !v$.country.$invalid"
        :filter-function="findCountry"
      />
      <PassportField
        v-model="passport"
        :errors="errors.passport"
        :is-valid="!v$.passport.$invalid"
        :disabled="false"
        :loading="ocrProcessing"
      />
      <PassportExpiryField
        v-model="passportExpiryDate"
        :errors="errors.passportExpiryDate"
        :is-valid="!v$.passportExpiryDate.$invalid"
        :passport-min-date="passportMinDate"
        :loading="ocrProcessing"
      />
      <MaritalStatusField
        v-if="isMaritalStatusEnabled"
        v-model="maritalStatus"
        :marital-statuses="maritalStatuses"
        :errors="errors.maritalStatus"
        :is-valid="!v$.maritalStatus.$invalid"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .visitor-form__inputs {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    gap: var(--spacing-5);
  }
</style>
